import { AutodeskConstants } from "../../autodeskConstants";
import { IClickEvent, IMarker } from "../../interfaces";
import { Point } from "../../models/point";
import { MarkerManager } from "../../utils/markerManager";

export interface IOnClickOptions {
	markerManager: MarkerManager;
	markers: IMarker[];
	onClick?: (eventContext: IClickEvent) => void;
	onRightClick?: (eventContext: IClickEvent) => void;
}

/**
 * Marker extension, handling (right)click events of markers
 *
 * @remarks see https://aps.autodesk.com/blog/custom-tools-forge-viewer for description of the public methods
 */
export class ClickExtension extends Autodesk.Viewing.Extension {
	private canvasRect: DOMRect;

	public constructor(public viewer: Autodesk.Viewing.GuiViewer3D, public clickOptions: IOnClickOptions) {
		super(viewer, clickOptions);

		this.onClickHandler = this.onClickHandler.bind(this);
		this.onRightClickHandler = this.onRightClickHandler.bind(this);

		if (this.viewer.canvas.getBoundingClientRect) {
			this.canvasRect = this.viewer.canvas.getBoundingClientRect();
		}
	}

	public load(): boolean {
		this.viewer.toolController.registerTool(this);
		this.viewer.toolController.activateTool(this.getName());

		return true;
	}

	public getName(): string {
		return AutodeskConstants.Extensions.Click;
	}

	public getNames(): string[] {
		return [this.getName()];
	}

	public unload(): boolean {
		this.viewer.toolController.deactivateTool(this.getName());
		this.viewer.toolController.deregisterTool(this);

		return true;
	}

	/**
	 * Handle a single left or right mouse click
	 *
	 * @param event click event
	 * @param button 0 = left click, 2 = right click
	 *
	 * @remarks This implementation is a modified copy of SceneTool.handleSingleClick().
	 * 	Most important is that the 'selected item' functionality is stripped, because
	 *  this made the 2nd click on a marker being ignored
	 *
	 * @returns true if click event should NOT be bubbled, false otherwise
	 */
	public handleSingleClick(event: any, button: number): boolean {
		if (button === 0 || button === 2) {
			const res = [];
			const vpVec = this.viewer.impl.clientToViewport(event.canvasX, event.canvasY);

			const renderer = (this.viewer.impl as any).renderer();
			const overlayIdTarget = renderer.getOverlayIdTarget?.();

			if (!overlayIdTarget) {
				return false;
			}

			const dbId = renderer.idAtPixel(vpVec.x, vpVec.y, res, [overlayIdTarget]);
			const clickedViewPoint = this.viewer.impl.hitTestViewport(vpVec.clone(), false);

			const clickEvent = {
				type: Autodesk.DataVisualization.Core.MOUSE_CLICK,
				dbId,
				clickInfo: clickedViewPoint,
				originalEvent: event,
			};

			if (button === 0) {
				return this.onClickHandler(clickEvent);
			} else if (button === 2) {
				return this.onRightClickHandler(clickEvent);
			}
		}

		return false;
	}

	/**
	 * Handles single tap (gesture).
	 * A single tap is handled like a single left mouse click.
	 *
	 * @param event
	 * @returns true if tap event should NOT be bubbled, false otherwise
	 */
	public handleSingleTap(event): boolean {
		// handle as single left click
		return this.handleSingleClick(event, 0);
	}

	private onClickHandler(event: any): boolean {
		if (!this.clickOptions.onClick) {
			return false;
		}

		let dbId = 0;

		if (event.dbId) {
			dbId = event.dbId;
		}

		const mapPosition = this.getMapPositionFromClickEvent(event);
		const marker = this.clickOptions.markerManager.getMarkerAtPosition(mapPosition, this.clickOptions.markers, dbId);

		this.clickOptions.onClick({ marker, position: Point.FromVector(mapPosition) });
		return false;
	}

	public onRightClickHandler(event: any): boolean {
		if (!this.clickOptions.onRightClick) {
			return false;
		}

		let dbId = 0;

		if (event.dbId) {
			dbId = event.dbId;
		}

		const mapPosition = this.getMapPositionFromClickEvent(event);
		const marker = this.clickOptions.markerManager.getMarkerAtPosition(mapPosition, this.clickOptions.markers, dbId);

		this.clickOptions.onRightClick({ marker, position: Point.FromVector(mapPosition) });
		return false;
	}

	private getMapPositionFromClickEvent(event: any): THREE.Vector3 {
		// with touch devices clientX/Y props are undefined. Using center.x/y instead
		const x = (event.originalEvent.clientX ?? event.originalEvent.center.x) - (this.canvasRect?.left ?? 0);
		const y = (event.originalEvent.clientY ?? event.originalEvent.center.y) - (this.canvasRect?.top ?? 0);

		// todo: check if full screen is active
		const position = this.viewer.impl.clientToViewport(x, y);
		const mapPosition = position.unproject(this.viewer.impl.camera);
		mapPosition.z = 0; // set to 0 since we are not using 3D
		return mapPosition;
	}
}
